'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getDashboardRoute } from '@/lib/auth';

type User = {
  id: number;
  email: string;
  user_type: string; // 'adminwic' | 'adminstore' | 'employee'
  nom?: string;
  prenom?: string;
  phone?: string;
};

type AuthContextType = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // On mount, load user and token from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();

    if (!res.ok) throw new Error(data.error || 'Login failed');

    // Build user object from response
    const userFromResponse: User = {
      ...data.user,
      ...(data.adminstore || {}),
      ...(data.adminwic || {}),
      ...(data.employee || {}),
    };

    // Save to state and localStorage
    setUser(userFromResponse);
    setToken(data.token);
    localStorage.setItem('user', JSON.stringify(userFromResponse));
    localStorage.setItem('token', data.token);
    document.cookie = 'token=' + data.token + '; path=/; max-age=604800; SameSite=Lax';

    // Redirect to the correct dashboard using the shared route map from lib/auth.
    router.push(getDashboardRoute(userFromResponse.user_type));
  };

  const register = async (userData: any) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    const data = await res.json();

    if (!res.ok) throw new Error(data.error || 'Registration failed');

    const userFromResponse: User = {
      ...data.user,
      ...(data.adminstore || {}),
      ...(data.adminwic || {}),
      ...(data.employee || {}),
    };

    setUser(userFromResponse);
    setToken(data.token);
    localStorage.setItem('user', JSON.stringify(userFromResponse));
    localStorage.setItem('token', data.token);
    router.push(getDashboardRoute(userFromResponse.user_type));
  };

 const logout = async () => {
  setUser(null);
  setToken(null);
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  document.cookie = 'token=; path=/; max-age=0';
  await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
  router.push('/');
};

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}